// src/lib/exportPdf.ts
import { jsPDF } from 'jspdf';

export type Pt={e:number;n:number};
export type Prim=
  |{k:'poly';pts:{e:number;n:number}[];close?:boolean;fill?:string;fillA?:number;
    stroke?:string;w?:number;dash?:number[];arrow?:boolean}
  |{k:'node';e:number;n:number;rMM:number;fill:string;shape:'circle'|'square'|'tri'}
  |{k:'text';e:number;n:number;s:string;pt:number;color:string}
  |{k:'img';el:HTMLImageElement;e:number;n:number;wM:number;hM:number;opacity:number};
export type PdfOpts={paper:'A1'|'A3'|'A4';landscape:boolean;scale:number|'fit'};

const PAPER:{[k:string]:[number,number]}={A1:[594,841],A3:[297,420],A4:[210,297]}; // mm portrait
const hex=(h:string):[number,number,number]=>{const m=h.replace('#','');
  return[parseInt(m.slice(0,2),16),parseInt(m.slice(2,4),16),parseInt(m.slice(4,6),16)];};
const polyPath=(doc:any,pts:{x:number;y:number}[],close:boolean,style:'S'|'F')=>{
  const segs=pts.slice(1).map((p,i)=>[p.x-pts[i].x,p.y-pts[i].y]);
  doc.lines(segs,pts[0].x,pts[0].y,[1,1],style,close);};

export function exportPdf(prims:Prim[],area:{e0:number;n0:number;e1:number;n1:number},
  opts:PdfOpts,fileName='drainage-plan.pdf'){
  const [p0,p1]=PAPER[opts.paper];
  const W=opts.landscape?p1:p0, H=opts.landscape?p0:p1;
  const M=10, fw=W-2*M, fh=H-2*M;
  const eMin=Math.min(area.e0,area.e1), eMax=Math.max(area.e0,area.e1);
  const nMin=Math.min(area.n0,area.n1), nMax=Math.max(area.n0,area.n1);
  const mmPerM=opts.scale==='fit'
    ?Math.min(fw/(eMax-eMin),fh/(nMax-nMin))
    :1000/(opts.scale as number);
  const ox=M+(fw-(eMax-eMin)*mmPerM)/2, oy=M+(fh-(nMax-nMin)*mmPerM)/2;
  const X=(e:number)=>ox+(e-eMin)*mmPerM, Y=(n:number)=>oy+(nMax-n)*mmPerM;

  const doc=new jsPDF({unit:'mm',format:[W,H],orientation:opts.landscape?'landscape':'portrait'});
  doc.saveGraphicsState();
  doc.rect(M,M,fw,fh); (doc as any).clip(); (doc as any).discardPath();

  for(const p of prims){
    if(p.k==='img'){
      // world -> page mm for top-left (north-west) corner
      const x=X(p.e), y=Y(p.n);
      const w=p.wM*mmPerM, h=p.hM*mmPerM;
      // draw via canvas so we can bake in opacity
      const c=document.createElement('canvas');
      c.width=p.el.width; c.height=p.el.height;
      const ctx=c.getContext('2d')!;
      ctx.globalAlpha=p.opacity;
      ctx.fillStyle='#ffffff'; ctx.fillRect(0,0,c.width,c.height); // white behind transparency
      ctx.drawImage(p.el,0,0);
      doc.addImage(c.toDataURL('image/jpeg',0.85),'JPEG',x,y,w,h);
      continue;
    }
    if(p.k==='poly'){
      const pts=p.pts.map(q=>({x:X(q.e),y:Y(q.n)}));
      if(pts.length<2)continue;
      doc.setLineDashPattern(p.dash?[...p.dash]:[],0);
      doc.setLineWidth(p.w??0.3);
      if(p.stroke){const[r,g,b]=hex(p.stroke);doc.setDrawColor(r,g,b);}
      if(p.fill){const[r,g,b]=hex(p.fill);doc.setFillColor(r,g,b);
        doc.setGState(new (doc as any).GState({opacity:p.fillA??1}));
        polyPath(doc,pts,true,'F');
        doc.setGState(new (doc as any).GState({opacity:1}));}
      if(p.stroke)polyPath(doc,pts,!!p.close,'S');
      if(p.arrow&&pts.length>=2){
        const a=pts[pts.length-2],b=pts[pts.length-1];
        const ang=Math.atan2(b.y-a.y,b.x-a.x),L=2.2,Wd=1.3;
        const[r,g,bl]=hex(p.stroke??'#000000');doc.setFillColor(r,g,bl);
        doc.setLineDashPattern([],0);
        doc.triangle(b.x,b.y,
          b.x-L*Math.cos(ang)+Wd*Math.sin(ang),b.y-L*Math.sin(ang)-Wd*Math.cos(ang),
          b.x-L*Math.cos(ang)-Wd*Math.sin(ang),b.y-L*Math.sin(ang)+Wd*Math.cos(ang),'F');}
    }else if(p.k==='node'){
      const x=X(p.e),y=Y(p.n),r=p.rMM;const[cr,cg,cb]=hex(p.fill);
      doc.setFillColor(cr,cg,cb);doc.setDrawColor(15,23,42);
      doc.setLineWidth(0.15);doc.setLineDashPattern([],0);
      if(p.shape==='circle')doc.circle(x,y,r,'FD');
      else if(p.shape==='square')doc.rect(x-r*0.85,y-r*0.85,r*1.7,r*1.7,'FD');
      else doc.triangle(x,y+r,x-r,y-r,x+r,y-r,'FD');   // outfall points down
    }else{
      const[r,g,b]=hex(p.color);doc.setTextColor(r,g,b);
      doc.setFontSize(p.pt);doc.text(p.s,X(p.e),Y(p.n));
    }
  }
  doc.restoreGraphicsState();
  doc.setDrawColor(0,0,0);doc.setLineWidth(0.35);doc.setLineDashPattern([],0);
  doc.rect(M,M,fw,fh);                                 // simple frame only
  doc.setTextColor(0,0,0);doc.setFontSize(8);
  doc.text(`Scale 1:${Math.round(1000/mmPerM)}`,W-M-2,H-M-3,{align:'right'});
  doc.save(fileName);
}